/**
 * Read risk-compliance events back from the ledger. Every other file in this module is pure; this
 * one does network I/O, so it stays thin and untested by unit test - verified live instead (see
 * `devdocs/features/risk-compliance/FEATURE.md`'s desk actions).
 *
 * Goes through `fetchEventsQuery` directly, not `fetchAssetEventsForAssets`
 * (`asset-register/asset-event-crud.js`) - that wrapper filters `types` against
 * `isReportReadableEventType`, a whitelist these new types were never added to. `fetchEventsQuery`
 * validates `types` as plain comma strings with no such list (`api/farmEvents.js`), and a live probe
 * (26 Sep 2026) confirmed the backend accepts and returns an unregistered type cleanly - this is
 * the first genuine reader against that fact.
 */

import { fetchEventsQuery } from 'api/farmEvents'
import { unwrapEvents } from 'helper-functions/asset-projection/assemble-snapshot'
import { RISK_COMPLIANCE_EVENT_TYPES } from './types'

const ALL_TYPES = Object.keys(RISK_COMPLIANCE_EVENT_TYPES)

export const READ_MAX_RESULTS = 1000

/**
 * **Do not rely on the order.** It is requested newest-first so that, if the ceiling bites, the
 * window keeps recent history, but same-`time` events come back in whatever order the API picks.
 * Every projection sorts through `project/replay-order.js` before it merges.
 *
 * **The ceiling is real, and the reader reports it rather than hiding it.** The events API has no
 * cursor (`asset-event-crud.js` documents the same limit), so a larger number only moves the point
 * where history is cut off. A read that returns exactly `maxResults` rows is flagged `truncated`, and
 * the tabs say so, the same way `assemble-snapshot.js` marks a snapshot.
 *
 * @param {{ tenant: string, farmId?: number, types?: string[], maxResults?: number }} input
 * @returns {Promise<{ events: Object[], truncated: boolean }>}
 */
export const fetchRiskComplianceEvents = async ({ tenant, farmId, types = ALL_TYPES, maxResults = READ_MAX_RESULTS } = {}) => {
    const response = await fetchEventsQuery({
        tenant,
        query: {
            types: types.join(','),
            ...(farmId != null ? { farmIds: String(farmId) } : {}),
            order: 'desc',
            maxResults,
        },
    })
    const events = unwrapEvents(response)
    return { events, truncated: events.length >= maxResults }
}

export default fetchRiskComplianceEvents
